'use client'

import classNames from 'classnames'

import { Image } from '@vinted/web-ui'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { clickEvent } from '_libs/common/event-tracker/events'
import { Screen } from 'constants/tracking/screens'
import { ClickableElement } from 'constants/tracking/clickable-elements'

type Props = {
  webImageSrc: string
  webImageSrcSet: string
  mobileImageSrc: string
  mobileImageSrcSet: string
  titleTranslationKey: string
  descriptionTranslationKey: string
  linkTranslationKey?: string
  linkTestId?: string
  index: number
}

const HowItWorksClosingSubSection = ({
  webImageSrc,
  webImageSrcSet,
  mobileImageSrc,
  mobileImageSrcSet,
  titleTranslationKey,
  descriptionTranslationKey,
  linkTranslationKey,
  index,
  linkTestId,
}: Props) => {
  const translate = useTranslate('dac7-2024.how_it_works')
  const urlLink = useTranslate('dac7-2024.links')
  const { track } = useTracking()

  const handleOnLinkClick = (linkUrl: string) => {
    track(
      clickEvent({
        screen: Screen.Dac7,
        target: ClickableElement.Hyperlink,
        targetDetails: `closing_link__${linkUrl}`,
      }),
    )
  }

  const renderLink = (url: string) => (
    <a key="link" href={url} onClick={() => handleOnLinkClick(url)} data-testid={linkTestId}>
      {translate('closing.link')}
    </a>
  )

  const renderDescription = () => {
    if (linkTranslationKey)
      return (
        <p className="dac7__howitworks-closing-description">
          {translate(descriptionTranslationKey, {
            link: renderLink(urlLink(linkTranslationKey)),
          })}
        </p>
      )

    return (
      <span
        className="dac7__howitworks-closing-description"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: translate(descriptionTranslationKey) }}
      />
    )
  }

  return (
    <section
      className={classNames('dac7__howitworks-closing', {
        'no-padding-top': index !== 0,
      })}
      data-testid="howitworks-closing-section"
    >
      <div className="dac7__howitworks-closing-image u-desktops-only">
        <Image
          src={webImageSrc}
          srcset={webImageSrcSet}
          scaling={Image.Scaling.Cover}
          loading="lazy"
        />
      </div>
      <div className="dac7__howitworks-closing-text-container">
        <h2 className="dac7__howitworks-closing-title">{translate(titleTranslationKey)}</h2>
        <div className="dac7__howitworks-closing-image u-mobiles-only">
          <Image
            src={mobileImageSrc}
            srcset={mobileImageSrcSet}
            scaling={Image.Scaling.Cover}
            loading="lazy"
          />
        </div>
        {renderDescription()}
      </div>
    </section>
  )
}

export default HowItWorksClosingSubSection
